// @ts-nocheck

import React, { useState, useEffect } from 'react';
import { MediaItem } from '../types';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface ProductFormComponentProps {
    item: MediaItem;
    onProductUpdate: (updatedItem: MediaItem) => void;
}

export const ProductFormComponent: React.FC<ProductFormComponentProps> = ({ item, onProductUpdate }) => {
    const [generating, setGenerating] = useState(false);
    const [formData, setFormData] = useState({
        name: item.name || '',
        description: item.description || '',
        cost: item.price?.cost || '',
        features: item.features || '',
        isPublished: item.isPublished || false
    });
    const [originalValues, setOriginalValues] = useState<any>(null);
    const [assistantHasEdited, setAssistantHasEdited] = useState(false);
    const [assistantSuggestionsDismissed, setAssistantSuggestionsDismissed] = useState(false);

    // Update form data when item changes
    useEffect(() => {
        setFormData({
            name: item.name || '',
            description: item.description || '',
            cost: item.price?.cost || '',
            features: item.features || '',
            isPublished: item.isPublished || false
        });
        setOriginalValues(null);
        setAssistantHasEdited(false);
        setAssistantSuggestionsDismissed(false);
    }, [item]);

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Update the item immediately with all form changes
        const updatedItem = {
            ...item,
            [field]: value
        };

        // Auto-calculate price.value when cost changes (40% markup)
        if (field === 'cost' && value) {
            const costValue = parseFloat(value);
            if (!isNaN(costValue)) {
                const calculatedValue = costValue + (costValue * 0.4);
                updatedItem.price = {
                    cost: value,
                    value: calculatedValue.toString()
                };

                // Update variants if they exist
                if (updatedItem.variants && updatedItem.variants.length > 0) {
                    updatedItem.variants = updatedItem.variants.map(variant => ({
                        ...variant,
                        price: calculatedValue,
                        cost: costValue
                    }));
                }
            }
        }

        onProductUpdate(updatedItem);
    };

    // Calculate display price from cost
    const getCalculatedPrice = (): string => {
        if (!formData.cost) return '';
        const costValue = parseFloat(formData.cost);
        if (isNaN(costValue)) return '';
        return (costValue + (costValue * 0.4)).toFixed(2);
    };

    // Calculate price value and synchronize variants (matching Angular app functionality)
    const calculatePriceValue = () => {
        if (item && item.price && item.price.cost) {
            const cost = parseFloat(item.price.cost);
            if (!isNaN(cost)) {
                // Calculate value using the same formula as in the pricing form (cost + 40% markup)
                const newPrice = cost + cost * 0.4;
                const updatedItem = {
                    ...item,
                    price: {
                        cost: item.price.cost,
                        value: newPrice.toString()
                    }
                };

                // Update variants if they exist
                if (updatedItem.variants && updatedItem.variants.length > 0) {
                    updatedItem.variants = updatedItem.variants.map(variant => ({
                        ...variant,
                        price: newPrice,
                        cost: cost
                    }));
                }

                onProductUpdate(updatedItem);
            }
        }
    };

    // AI Generation Functions
    const generateProductDetails = async () => {
        try {
            setGenerating(true);

            // Store original values if not already stored
            if (!originalValues) {
                setOriginalValues({
                    description: formData.description,
                    name: formData.name,
                    features: formData.features
                });
            }

            const functions = getFunctions();
            const makeProductDetails = httpsCallable(functions, 'AgentServices-makeProductDetails');

            const result = await makeProductDetails({
                product: item,
                caption: formData.description
            });

            if (result.data) {
                const newFormData = {
                    ...formData,
                    description: result.data.description,
                    features: result.data.features.join('\n'),
                    name: result.data.name
                };

                setFormData(newFormData);
                setAssistantHasEdited(true);
                setAssistantSuggestionsDismissed(false);

                // Update the item with new data
                const updatedItem = {
                    ...item,
                    name: newFormData.name,
                    description: newFormData.description,
                    features: newFormData.features
                };
                onProductUpdate(updatedItem);
            }
        } catch (error) {
            console.error('Error generating product details:', error);
            alert('There was an error generating product details');
        } finally {
            setGenerating(false);
        }
    };


    const dismissSuggestions = () => {
        setAssistantSuggestionsDismissed(true);
    };

    const hasAssistantSuggestions = () => {
        return assistantHasEdited && !assistantSuggestionsDismissed;
    };



    return (
        <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Product Details</h3>

                {/* AI Assistant Suggestions */}
                {hasAssistantSuggestions() && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-medium text-blue-800">AI Assistant has updated your product details</span>
                            </div>
                            <button
                                onClick={dismissSuggestions}
                                className="text-blue-500 hover:text-blue-700"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {/* AI Generation Button */}
                <div className="mb-6">
                    <button
                        onClick={generateProductDetails}
                        disabled={generating}
                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                        {generating ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        )}
                        {generating ? 'Generating...' : 'Generate with AI'}
                    </button>
                </div>

                <div className="space-y-6">
                    {/* Product Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Product Name
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            placeholder="Enter product name..."
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            rows={12}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                            placeholder="Describe your product..."
                        />
                    </div>

                    {/* Cost and Calculated Price */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Cost
                            </label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₦</span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.cost}
                                    onChange={(e) => handleInputChange('cost', e.target.value)}
                                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="0.00"
                                />
                            </div>
                        </div>

                        {/* Calculated Price Display */}
                        {formData.cost && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Selling Price (Cost + 40% markup)
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₦</span>
                                    <input
                                        type="text"
                                        value={getCalculatedPrice()}
                                        readOnly
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Features */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Features
                        </label>
                        <textarea
                            value={formData.features}
                            onChange={(e) => handleInputChange('features', e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                            placeholder="Describe the key features..."
                        />
                    </div>

                    {/* Published Status Toggle */}
                    <div>
                        <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                                Published Status
                            </label>
                            <div className="flex items-center">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.isPublished}
                                        onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                                    <span className="ml-3 text-sm font-medium text-gray-700">
                                        {formData.isPublished ? 'Published' : 'Draft'}
                                    </span>
                                </label>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            {formData.isPublished
                                ? 'This product is visible to customers'
                                : 'This product is saved as a draft and not visible to customers'
                            }
                        </p>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default ProductFormComponent;
